// pages/CompetitorsPage.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNotification } from "../context/NotificationContext";
import { PageHeader, Card, StatCard, ConfirmModal, Toggle } from "../components/UI";
import * as api from "../services/dynamicPriceApi";

const companyId = localStorage.getItem("companyid") || 1;

const PLATFORMS = ["MakeMyTrip", "Booking.com", "Expedia", "Goibibo", "Agoda"];

const PLATFORM_META = {
  "MakeMyTrip":  { color: "#dc2626", abbr: "MMT" },
  "Booking.com": { color: "#003b95", abbr: "BDC" },
  "Expedia":     { color: "#fbbf24", abbr: "EXP", dark: true },
  "Goibibo":     { color: "#7c3aed", abbr: "GIB" },
  "Agoda":       { color: "#0891b2", abbr: "AGD" },
};

const STATUS_CFG = {
  active:   { color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0", dot: "#16a34a", label: "Active"   },
  inactive: { color: "#475569", bg: "#f8fafc", border: "#e2e8f0", dot: "#94a3b8", label: "Inactive" },
  error:    { color: "#b91c1c", bg: "#fef2f2", border: "#fecaca", dot: "#dc2626", label: "Error"    },
};

// ── Pure helpers ──────────────────────────────────────────────
const fmt = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR", maximumFractionDigits: 0,
  }).format(n);
};

const normPrices = (raw) => {
  if (!raw || typeof raw !== "object") return {};
  const out = {};
  Object.entries(raw).forEach(([k, v]) => {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) out[k] = n;
  });
  return out;
};

const normComp = (r) => ({
  id:             r?.id,
  name:           r?.name          || "Unnamed",
  platform:       r?.platform      || "Unknown",
  city:           r?.city          || "",
  url:            r?.url           || "",
  status:         String(r?.status || "inactive").toLowerCase(),
  prices:         normPrices(r?.prices),
  last_scraped_at:r?.last_scraped_at || null,
  last_error:     r?.last_error    || "",
});

const fmtTime = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const diffMin = Math.round((now - d) / 60000);
  if (diffMin < 1)   return "Just now";
  if (diffMin < 60)  return `${diffMin}m ago`;
  if (diffMin < 1440)return `${Math.round(diffMin / 60)}h ago`;
  if (diffMin < 2880)return "Yesterday";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

// ── Sub-components ────────────────────────────────────────────

function Shimmer({ h = 20, w = "100%", r = 8 }) {
  return (
    <div style={{
      height: h, width: w, borderRadius: r,
      background: "linear-gradient(90deg,#f1f5f9 25%,#e8edf2 50%,#f1f5f9 75%)",
      backgroundSize: "400% 100%",
      animation: "dpShimmer 1.4s ease infinite",
    }} />
  );
}

function StatusPill({ status }) {
  const c = STATUS_CFG[status] || STATUS_CFG.inactive;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "2px 10px", borderRadius: 20,
      fontSize: 10, fontWeight: 700,
      color: c.color, background: c.bg, border: `1px solid ${c.border}`,
      letterSpacing: 0.2,
    }}>
      <span style={{
        width: 5, height: 5, borderRadius: "50%",
        background: c.dot, flexShrink: 0,
        boxShadow: status === "active" ? `0 0 0 2px ${c.dot}33` : "none",
      }} />
      {c.label}
    </span>
  );
}

function PlatformIcon({ platform }) {
  const meta = PLATFORM_META[platform] || { color: "#64748b", abbr: "OTA" };
  return (
    <div style={{
      width: 40, height: 40, borderRadius: 12, flexShrink: 0,
      background: meta.color,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
      color: meta.dark ? "#1e293b" : "#fff",
      boxShadow: `0 2px 8px ${meta.color}40`,
    }}>
      {meta.abbr}
    </div>
  );
}

// Horizontal price bar with label
function PriceRow({ label, value, maxVal, rank }) {
  const pct = maxVal > 0 ? Math.round((value / maxVal) * 100) : 0;
  const barColor = rank === 0 ? "#16a34a" : rank === 1 ? "#2563eb" : "#8b5cf6";
  return (
    <div style={{ marginBottom: 9 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <span style={{
          fontSize: 11, color: "#64748b", fontWeight: 500,
          maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{label}</span>
        <span style={{
          fontFamily: "'DM Mono',monospace", fontSize: 12, fontWeight: 700, color: "#0f172a",
        }}>{fmt(value)}</span>
      </div>
      <div style={{ height: 4, background: "#f1f5f9", borderRadius: 4, overflow: "hidden" }}>
        <div style={{
          height: "100%", width: `${pct}%`, borderRadius: 4,
          background: barColor, transition: "width 0.5s cubic-bezier(.16,1,.3,1)",
        }} />
      </div>
    </div>
  );
}

// ── Competitor Card (grid item) ───────────────────────────────
function CompetitorCard({ comp, idx, onScrape, onToggle, onDelete, scraping, toggling }) {
  const [open, setOpen] = useState(false);
  const isScrapingThis = scraping === comp.id;
  const isActive = comp.status === "active";
  const priceEntries = Object.entries(comp.prices).sort((a, b) => a[1] - b[1]);
  const hasPrice = priceEntries.length > 0;
  const maxPrice = hasPrice ? Math.max(...priceEntries.map(([, v]) => v)) : 0;
  const minPrice = hasPrice ? priceEntries[0][1] : null;
  const sc = STATUS_CFG[comp.status] || STATUS_CFG.inactive;

  return (
    <div
      className="dp-fade-in"
      style={{
        background: "#fff",
        borderRadius: 16,
        border: `1px solid ${comp.status === "error" ? "#fecaca" : "#e8edf2"}`,
        boxShadow: "0 1px 6px rgba(15,23,42,0.06)",
        overflow: "hidden",
        transition: "box-shadow 0.15s, transform 0.15s",
        animationDelay: `${idx * 0.05}s`,
        animationFillMode: "both",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = "0 4px 20px rgba(15,23,42,0.1)";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = "0 1px 6px rgba(15,23,42,0.06)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      {/* Active accent stripe */}
      <div style={{
        height: 3,
        background: isActive
          ? "linear-gradient(90deg,#2563eb,#16a34a)"
          : comp.status === "error"
          ? "#fca5a5"
          : "#e2e8f0",
      }} />

      <div style={{ padding: "20px 22px" }}>
        {/* Row 1: identity */}
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 16 }}>
          <PlatformIcon platform={comp.platform} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 3 }}>
              <span style={{
                fontSize: 14, fontWeight: 700, color: "#0f172a",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 180,
              }} title={comp.name}>
                {comp.name}
              </span>
              <StatusPill status={comp.status} />
            </div>
            <div style={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.5 }}>
              {comp.platform}{comp.city ? ` · ${comp.city}` : ""}
            </div>
          </div>

          {/* Toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>
              {isActive ? "On" : "Off"}
            </span>
            <Toggle
              on={isActive}
              onToggle={() => onToggle(comp.id)}
              disabled={toggling}
            />
          </div>
        </div>

        {/* Price headline */}
        {hasPrice ? (
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "flex-end",
            marginBottom: 16, padding: "12px 16px",
            background: "linear-gradient(135deg,#f8fafc,#f1f5f9)",
            borderRadius: 10, border: "1px solid #e8edf2",
          }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8",
                textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 3 }}>
                Starting From
              </div>
              <div style={{
                fontFamily: "'DM Mono',monospace", fontSize: 22, fontWeight: 800,
                color: "#0f172a", lineHeight: 1,
              }}>
                {fmt(minPrice)}
              </div>
              <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>/ night</div>
            </div>
            {fmtTime(comp.last_scraped_at) && (
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600,
                  textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>
                  Updated
                </div>
                <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>
                  {fmtTime(comp.last_scraped_at)}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{
            marginBottom: 16, padding: "12px 14px",
            background: isActive ? "#fffbeb" : "#f8fafc",
            border: `1px solid ${isActive ? "#fde68a" : "#e2e8f0"}`,
            borderRadius: 10, fontSize: 12,
            color: isActive ? "#92400e" : "#94a3b8",
          }}>
            {isActive
              ? "⏳ No prices yet — run a scrape to fetch rates"
              : "Enable this competitor to start tracking prices"}
          </div>
        )}

        {/* Price bars — first room always visible */}
        {hasPrice && (
          <div style={{ marginBottom: 14 }}>
            {priceEntries.slice(0, open ? undefined : 1).map(([name, price], i) => (
              <PriceRow key={name} label={name} value={price} maxVal={maxPrice} rank={i} />
            ))}
            {priceEntries.length > 1 && (
              <button
                onClick={() => setOpen(o => !o)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  fontSize: 11, fontWeight: 700, color: "#2563eb",
                  padding: "2px 0", display: "flex", alignItems: "center", gap: 4,
                }}
              >
                {open
                  ? "▲ Show less"
                  : `▼ ${priceEntries.length - 1} more room type${priceEntries.length > 2 ? "s" : ""}`}
              </button>
            )}
          </div>
        )}

        {/* Error message */}
        {comp.status === "error" && comp.last_error && (
          <div style={{
            marginBottom: 14, padding: "8px 12px",
            background: "#fef2f2", border: "1px solid #fecaca",
            borderRadius: 8, fontSize: 11, color: "#b91c1c",
            display: "flex", gap: 6, alignItems: "flex-start",
          }}>
            <span style={{ flexShrink: 0 }}>⚠</span>
            <span style={{ lineHeight: 1.5 }}>{comp.last_error}</span>
          </div>
        )}

        {/* URL truncated */}
        {comp.url && (
          <div style={{
            fontSize: 10, color: "#cbd5e1", marginBottom: 16,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }} title={comp.url}>
            🔗 {comp.url}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="dp-btn dp-btn-outline dp-btn-sm"
            style={{ flex: 1 }}
            onClick={() => onScrape(comp.id)}
            disabled={!!scraping || !isActive}
            title={!isActive ? "Enable to scrape" : "Fetch latest prices now"}
          >
            {isScrapingThis
              ? <><span className="dp-loader" style={{ width: 11, height: 11, borderWidth: 1.5 }} /> Scraping…</>
              : "🔄 Scrape Now"}
          </button>
          <button
            className="dp-btn dp-btn-danger dp-btn-sm"
            onClick={() => onDelete(comp.id)}
            title="Remove competitor"
          >
            🗑
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Add Competitor Drawer ─────────────────────────────────────
function AddDrawer({ onClose, onSave, saving }) {
  const [form, setForm] = useState({
    name: "", platform: "MakeMyTrip", city: "Chandigarh", url: "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const valid = form.name.trim() && form.url.trim() && form.platform;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 200,
          background: "rgba(15,23,42,0.45)", backdropFilter: "blur(3px)",
        }}
      />

      {/* Panel */}
      <div style={{
        position: "fixed", right: 0, top: 0, bottom: 0, width: 440,
        zIndex: 201, background: "#fff", overflowY: "auto",
        boxShadow: "-12px 0 48px rgba(15,23,42,0.14)",
        display: "flex", flexDirection: "column",
        animation: "dpSlideIn 0.22s cubic-bezier(.16,1,.3,1)",
      }}>
        {/* Header */}
        <div style={{
          padding: "26px 28px 20px",
          borderBottom: "1px solid #f1f5f9",
          display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        }}>
          <div>
            <h4 style={{
              fontFamily: "'Playfair Display',serif", fontSize: 20,
              fontWeight: 700, color: "#0f172a", margin: 0,
            }}>
              Add Competitor
            </h4>
            <p style={{ margin: "5px 0 0", fontSize: 12, color: "#64748b" }}>
              Track a hotel's pricing on any platform
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "#f1f5f9", border: "none", borderRadius: 8,
              width: 32, height: 32, cursor: "pointer", fontSize: 15, color: "#64748b",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0, transition: "background 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "#e2e8f0"}
            onMouseLeave={e => e.currentTarget.style.background = "#f1f5f9"}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "24px 28px", flex: 1 }}>
          <div style={{ marginBottom: 18 }}>
            <label className="dp-label">Hotel / Property Name *</label>
            <input
              className="dp-input"
              placeholder="e.g. Taj Chandigarh"
              value={form.name}
              onChange={e => set("name", e.target.value)}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 18 }}>
            <div>
              <label className="dp-label">Platform *</label>
              <select className="dp-select" value={form.platform} onChange={e => set("platform", e.target.value)}>
                {PLATFORMS.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="dp-label">City</label>
              <input
                className="dp-input"
                placeholder="e.g. Chandigarh"
                value={form.city}
                onChange={e => set("city", e.target.value)}
              />
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label className="dp-label">Listing URL *</label>
            <input
              className="dp-input"
              placeholder="https://www.makemytrip.com/hotels/…"
              value={form.url}
              onChange={e => set("url", e.target.value)}
            />
            <div style={{ marginTop: 6, fontSize: 11, color: "#94a3b8", lineHeight: 1.5 }}>
              Paste the full hotel page URL from the platform
            </div>
          </div>

          {/* Platform quick-select visual buttons */}
          <div style={{ marginBottom: 24 }}>
            <div className="dp-label" style={{ marginBottom: 10 }}>Quick Select Platform</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {PLATFORMS.map(p => {
                const meta = PLATFORM_META[p] || { color: "#64748b", abbr: "OTA" };
                const selected = form.platform === p;
                return (
                  <button
                    key={p}
                    onClick={() => set("platform", p)}
                    style={{
                      padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                      fontSize: 11, fontWeight: 700, transition: "all 0.15s",
                      background: selected ? meta.color : "#f8fafc",
                      color: selected ? (meta.dark ? "#1e293b" : "#fff") : "#64748b",
                      boxShadow: selected ? `0 2px 8px ${meta.color}40` : "none",
                    }}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Info callout */}
          <div style={{
            padding: "12px 16px",
            background: "#f0f7ff", border: "1px solid #bfdbfe",
            borderRadius: 10, fontSize: 12, color: "#1e40af", lineHeight: 1.6,
          }}>
            <strong>💡 Note:</strong> New competitors are added as <em>Inactive</em>.
            Toggle them on and run a scrape to start fetching live prices.
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: "16px 28px", borderTop: "1px solid #f1f5f9",
          display: "flex", gap: 10,
        }}>
          <button className="dp-btn dp-btn-ghost" style={{ flex: 1 }} onClick={onClose}>
            Cancel
          </button>
          <button
            className="dp-btn dp-btn-primary"
            style={{ flex: 2 }}
            onClick={() => onSave(form)}
            disabled={!valid || saving}
          >
            {saving
              ? <><span className="dp-spin">⟳</span> Adding…</>
              : "Add Competitor"}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Loading skeleton grid ─────────────────────────────────────
function SkeletonGrid() {
  return (
    <>
      <style>{`
        @keyframes dpShimmer {
          0%   { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
      `}</style>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))", gap: 16 }}>
        {[1, 2, 3].map(i => (
          <div key={i} style={{
            background: "#fff", borderRadius: 16, padding: 22,
            border: "1px solid #e8edf2", overflow: "hidden",
          }}>
            <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 20 }}>
              <Shimmer h={40} w={40} r={12} />
              <div style={{ flex: 1 }}>
                <Shimmer h={14} w="60%" r={6} />
                <div style={{ height: 6 }} />
                <Shimmer h={10} w="40%" r={5} />
              </div>
            </div>
            <Shimmer h={72} r={10} />
            <div style={{ height: 14 }} />
            <Shimmer h={32} r={8} />
          </div>
        ))}
      </div>
    </>
  );
}

// ── Empty state ───────────────────────────────────────────────
function EmptyState({ onAdd }) {
  return (
    <div style={{
      textAlign: "center", padding: "64px 32px",
      background: "#fff", borderRadius: 16,
      border: "1.5px dashed #e2e8f0",
    }}>
      <div style={{ fontSize: 52, marginBottom: 16 }}>🏨</div>
      <h4 style={{
        fontFamily: "'Playfair Display',serif", fontSize: 20,
        fontWeight: 700, color: "#0f172a", margin: "0 0 10px",
      }}>
        No Competitors Tracked Yet
      </h4>
      <p style={{ fontSize: 13, color: "#64748b", maxWidth: 360, margin: "0 auto 28px", lineHeight: 1.7 }}>
        Add competitors to benchmark your pricing. The AI uses their live rates
        to keep you positioned optimally in the market.
      </p>
      <button className="dp-btn dp-btn-primary" onClick={onAdd}>
        + Add Your First Competitor
      </button>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function CompetitorsPage() {
  const { notify } = useNotification();

  const [comps,       setComps]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [scraping,    setScraping]    = useState(null);  // id | "all" | null
  const [togglingId,  setTogglingId]  = useState(null);
  const [delId,       setDelId]       = useState(null);
  const [deletingId,  setDeletingId]  = useState(null);
  const [showAdd,     setShowAdd]     = useState(false);
  const [adding,      setAdding]      = useState(false);
  const [filterTab,   setFilterTab]   = useState("all"); // all | active | inactive | error

  // ── Data loading ─────────────────────────────────────────
  const loadCompetitors = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await api.getCompetitors(companyId);
      const list = res?.data?.data ?? res?.data ?? [];
      setComps(Array.isArray(list) ? list.map(normComp) : []);
    } catch {
      notify("error", "Failed to load competitors.");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { loadCompetitors(); }, [loadCompetitors]);

  // ── Derived ───────────────────────────────────────────────
  const active   = useMemo(() => comps.filter(c => c.status === "active"),   [comps]);
  const inactive = useMemo(() => comps.filter(c => c.status === "inactive"), [comps]);
  const errored  = useMemo(() => comps.filter(c => c.status === "error"),    [comps]);

  const marketAvg = useMemo(() => {
    const lows = active
      .map(c => Object.values(c.prices))
      .filter(a => a.length)
      .map(a => Math.min(...a));
    if (!lows.length) return 0;
    return Math.round(lows.reduce((a, b) => a + b, 0) / lows.length);
  }, [active]);

  const filtered = useMemo(() =>
    filterTab === "all" ? comps : comps.filter(c => c.status === filterTab),
    [comps, filterTab]
  );

  // ── Handlers ─────────────────────────────────────────────
  const handleScrape = async (id) => {
    setScraping(id);
    try {
      await api.scrapeCompetitor(companyId, id);
      notify("success", "Prices updated successfully");
      await loadCompetitors();
    } catch {
      notify("error", "Scrape failed — check the URL or try again");
    } finally {
      setScraping(null);
    }
  };

  const handleScrapeAll = async () => {
    if (!active.length) { notify("info", "No active competitors to scrape"); return; }
    setScraping("all");
    try {
      await api.scrapeAllCompetitors(companyId);
      notify("success", `${active.length} competitor${active.length > 1 ? "s" : ""} refreshed`);
      await loadCompetitors();
    } catch {
      notify("error", "Bulk scrape failed");
    } finally {
      setScraping(null);
    }
  };

  const handleToggle = async (id) => {
    const comp = comps.find(c => c.id === id);
    if (!comp) return;
    setTogglingId(id);
    try {
      await api.toggleCompetitor(companyId, id);
      const next = comp.status === "active" ? "inactive" : "active";
      notify("info", `${comp.name} is now ${next}`);
      await loadCompetitors();
    } catch {
      notify("error", "Status update failed");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!delId) return;
    setDeletingId(delId);
    try {
      await api.deleteCompetitor(companyId, delId);
      notify("success", "Competitor removed");
      setDelId(null);
      await loadCompetitors();
    } catch {
      notify("error", "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const handleAdd = async (form) => {
    setAdding(true);
    try {
      await api.addCompetitor(companyId, form);
      notify("success", `${form.name} added — enable it to start tracking`);
      setShowAdd(false);
      await loadCompetitors();
    } catch {
      notify("error", "Failed to add competitor");
    } finally {
      setAdding(false);
    }
  };

  // ── Render ───────────────────────────────────────────────
  return (
    <div style={{ padding: "28px 32px" }}>

      {/* Page header */}
      <PageHeader
        title="Competitor Tracking"
        subtitle="Live market pricing intelligence — scraped from OTA listings"
      >
        <button
          className="dp-btn dp-btn-outline"
          onClick={handleScrapeAll}
          disabled={!!scraping || !active.length}
        >
          {scraping === "all"
            ? <><span className="dp-spin">⟳</span> Refreshing…</>
            : "🔄 Refresh All"}
        </button>
        <button className="dp-btn dp-btn-primary" onClick={() => setShowAdd(true)}>
          + Add Competitor
        </button>
      </PageHeader>

      {/* KPI row — StatCard matches dashboard */}
      <div style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap" }}>
        <StatCard
          label="Total Tracked"
          value={comps.length}
          sub="across all platforms"
          color="#2563eb" icon="📊"
        />
        <StatCard
          label="Active"
          value={active.length}
          sub="live scraping enabled"
          color="#16a34a" icon="✓"
        />
        <StatCard
          label="Inactive"
          value={inactive.length}
          sub="tracking paused"
          color="#64748b" icon="⏸"
        />
        <StatCard
          label="Scraper Errors"
          value={errored.length}
          sub={errored.length ? "need attention" : "all clear"}
          color={errored.length ? "#dc2626" : "#94a3b8"} icon="⚠"
        />
        <StatCard
          label="Market Avg Low"
          value={marketAvg ? fmt(marketAvg) : "—"}
          sub={marketAvg ? "cheapest room avg" : "scrape to populate"}
          color="#8b5cf6" icon="📈"
        />
      </div>

      {/* Error banner */}
      {errored.length > 0 && filterTab !== "error" && (
        <div style={{
          marginBottom: 16, padding: "11px 18px",
          background: "#fef2f2", border: "1px solid #fecaca",
          borderRadius: 10, display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#b91c1c" }}>
            ⚠ {errored.length} competitor{errored.length > 1 ? "s have" : " has"} scraping errors
          </span>
          <button
            onClick={() => setFilterTab("error")}
            style={{ background: "none", border: "none", cursor: "pointer",
              color: "#b91c1c", fontWeight: 700, fontSize: 12, textDecoration: "underline" }}
          >
            View →
          </button>
        </div>
      )}

      {/* Filter pill tabs */}
      {comps.length > 0 && (
        <div style={{
          display: "flex", gap: 3, marginBottom: 20,
          background: "#f8fafc", borderRadius: 10,
          border: "1px solid #e8edf2", padding: 3,
          width: "fit-content",
        }}>
          {[
            { key: "all",      label: `All`,                count: comps.length   },
            { key: "active",   label: `Active`,             count: active.length  },
            { key: "inactive", label: `Inactive`,           count: inactive.length},
            ...(errored.length ? [{ key: "error", label: "Errors", count: errored.length }] : []),
          ].map(({ key, label, count }) => {
            const sel = filterTab === key;
            return (
              <button
                key={key}
                onClick={() => setFilterTab(key)}
                style={{
                  padding: "6px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                  fontSize: 12, fontWeight: 600, transition: "all 0.15s",
                  background: sel ? "#fff" : "transparent",
                  color: sel ? "#0f172a" : "#94a3b8",
                  boxShadow: sel ? "0 1px 4px rgba(15,23,42,0.08)" : "none",
                }}
              >
                {label}
                <span style={{
                  marginLeft: 5, fontSize: 10, fontWeight: 700,
                  color: sel ? "#2563eb" : "#cbd5e1",
                }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Loading */}
      {loading && <SkeletonGrid />}

      {/* Empty state */}
      {!loading && comps.length === 0 && (
        <EmptyState onAdd={() => setShowAdd(true)} />
      )}

      {/* Filtered empty state */}
      {!loading && comps.length > 0 && filtered.length === 0 && (
        <div style={{
          textAlign: "center", padding: "40px 20px",
          background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0",
        }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>🔍</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#64748b" }}>
            No {filterTab} competitors
          </div>
        </div>
      )}

      {/* Competitors grid */}
      {!loading && filtered.length > 0 && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: 16,
        }}>
          {filtered.map((comp, idx) => (
            <CompetitorCard
              key={comp.id}
              comp={comp}
              idx={idx}
              onScrape={handleScrape}
              onToggle={handleToggle}
              onDelete={(id) => setDelId(id)}
              scraping={scraping}
              toggling={togglingId === comp.id}
            />
          ))}
        </div>
      )}

      {/* Add drawer */}
      {showAdd && (
        <AddDrawer
          onClose={() => setShowAdd(false)}
          onSave={handleAdd}
          saving={adding}
        />
      )}

      {/* Confirm delete */}
      <ConfirmModal
        show={!!delId}
        title="Remove Competitor?"
        message="This will permanently delete all scraped price history for this competitor. This cannot be undone."
        confirmLabel={deletingId ? "Removing…" : "Yes, Remove"}
        confirmClass="dp-btn dp-btn-danger"
        onConfirm={handleDelete}
        onCancel={() => setDelId(null)}
      />
    </div>
  );
}